import { useCallback, useState } from 'react'
import { api } from '../api/client'
import type { DipAssessment, DipBlock } from '../api/client'
import { useHasPermission } from '../api/session'
import { usePageActions } from '../AppLayout'
import { useItemQuery } from '../hooks/useItemQuery'
import { useZielwaehrung } from '../hooks/useZielwaehrung'
import { Card } from '../components/card/Card'
import { Chip } from '../components/common/Chip'
import type { ChipTone } from '../components/common/Chip'
import { EmptyState, ErrorBox } from '../components/common/Feedback'
import { PermissionNotice } from '../components/common/PermissionNotice'
import { Refs, instrumentRef } from '../components/common/Refs'
import { DataTable } from '../components/table/DataTable'
import type { Column } from '../components/table/types'
import { alsGrossbetrag, Geld, geldtext, tag } from './priceFormat'
import s from './BuyTheDipPage.module.scss'

const PERMISSION = 'report:read'

/** Wie viele Zeilen ohne Scrollen stehen bleiben. */
const SICHTBARE_ZEILEN = 10

/**
 * Buy the Dip — abverkaufte Qualitätsaktien mit beginnender Bodenbildung.
 *
 * DER ZWEITE BLICK NEBEN DEM DAILY STRETCH, und absichtlich ein anderer: Der
 * Stretch fragt nach dem heutigen Tag, gemessen in ATR, mit Gates und Vetos.
 * Dieser Screen fragt nach der Jahreslage und rechnet ein Punktesystem — den
 * Algorithmus aus POC v1, unverändert. Wer beide auf denselben Titeln sieht,
 * erkennt den Unterschied zwischen den Verfahren.
 */

const zahl = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 1, maximumFractionDigits: 1,
})

function komma(wert: string | null): string {
  if (wert === null) return '—'
  const n = Number(wert)
  return Number.isFinite(n) ? zahl.format(n) : wert
}

function mitVorzeichen(wert: string | null): string {
  if (wert === null) return '—'
  const n = Number(wert)
  if (!Number.isFinite(n)) return wert
  return n.toLocaleString('de-DE', {
    minimumFractionDigits: 1, maximumFractionDigits: 1,
    signDisplay: 'exceptZero',
  })
}

/**
 * Deutsche Namen für das, was das Backend in ASCII führt.
 *
 * Der Python-Code hält sich an ASCII — „Liquiditaet" ist dort richtig und in
 * der Oberfläche falsch. Die Übersetzung gehört hierher und nicht ins DTO:
 * Der MCP-Zugang und die Schnittstelle sollen technische Namen bekommen,
 * keine für Menschen gesetzten.
 */
const BLOCK_NAMEN: Record<string, string> = {
  Fundamental: 'Fundamental',
  Abverkauf: 'Abverkauf',
  Boden: 'Boden',
  Liquiditaet: 'Liquidität',
}

/** Die Stufen der Bodenbildung, von der schlechtesten zur besten. */
const STRUKTUR: Record<string, string> = {
  neues_tief: 'macht neue Tiefs',
  fallende_tiefs: 'Tiefs fallen weiter',
  zwei_tage_ohne_neues_tief: 'zwei Tage ohne neues Tief',
  higher_low: 'höheres Tief',
  hl_und_hh: 'höheres Tief UND höheres Hoch',
  break_widerstand: 'Widerstand gebrochen',
}

/** Wohin der RSI zeigt. */
const RICHTUNG: Record<string, string> = {
  faellt: 'fällt',
  flach: 'flach',
  steigt_1_2: 'steigt seit ein bis zwei Tagen',
  steigt_3plus: 'steigt seit drei Tagen oder länger',
  cross_hoch: 'kreuzt nach oben',
}

/** Ein Grund in lesbarer Form — technische Kürzel werden übersetzt. */
function grund(text: string): string {
  return STRUKTUR[text] ?? RICHTUNG[text] ?? text
}

function statusTon(status: string): ChipTone {
  if (status === 'DIP CONFIRMED') return 'good'
  if (status === 'DIP SETUP') return 'info'
  if (status === 'WATCH') return 'warn'
  return 'neutral'
}

/**
 * Punkte gegen das ERREICHBARE, nicht gegen das Volle.
 *
 * Ein Titel mit dreißig Bars kann den Boden-Block nicht voll ausschöpfen — er
 * ist dann nicht schlecht bewertet, sondern unzureichend belegt. Steht dort
 * nur „18", liest man es als Wertung; „18/27" sagt, was gemeint ist.
 *
 * `soll` IST NÖTIG, weil der Nenner allein nichts verrät: Der Boden-Block hat
 * regulär 35 Punkte, der Fundamentalblock 30. Ohne den Vergleichswert stünde
 * die Datenreife-Fahne an jedem Block, weil jeder unter 100 liegt — beim
 * ersten Anlauf tat sie genau das.
 */
function Punkte({ punkte, maximum, soll }: {
  punkte: number; maximum: number; soll: number
}) {
  const gesenkt = maximum < soll
  return (
    <span className={s.punkte}>
      {punkte}
      <span className={s.von}>/{maximum}</span>
      {gesenkt && (
        <span className={s.reife}
              title={`von regulär ${soll} — Terme fielen mangels Daten aus`}>
          {' ⚑'}
        </span>
      )}
    </span>
  )
}

/** Ein Block eines ausgeschlossenen Titels zeigt nichts, nicht „0/0". */
function Blockpunkte({ zeile, block, soll }: {
  zeile: DipAssessment; block: DipBlock; soll: number
}) {
  if (zeile.excluded) return <span className={s.fehlt}>—</span>
  return <Punkte punkte={block.points} maximum={block.maximum} soll={soll} />
}

const COLUMNS: Column<DipAssessment>[] = [
  { key: 'name', label: 'Instrument', minWidth: '210px',
    // DIESELBE ZELLE WIE IN MELDUNGEN UND WATCHLIST: Ein Klick zeigt die
    // Eckdaten des Titels, statt sofort wegzunavigieren.
    //
    // OHNE ISIN ODER NAME bleibt es beim schlichten Text: Beide Felder
    // dürfen leer sein, und ein Popover ohne Ziel hätte nichts zu zeigen.
    render: (row) => (row.isin && row.name
      ? <Refs leer="—"
              refs={[instrumentRef(row.isin, row.name, row.instrument_id)]} />
      : <span className={s.name}>{row.name ?? '—'}</span>),
    help: 'Der beobachtete Titel. Ein Klick zeigt seine Eckdaten — Kennung, Kurs, Ampel und die jüngsten Meldungen.' },
  { key: 'status', label: 'Status', minWidth: '140px',
    render: (row) => row.excluded
      ? <Chip>ausgeschlossen</Chip>
      : <Chip tone={statusTon(row.status)}>{row.status}</Chip>,
    help: 'DIP CONFIRMED ab 85 Punkten, DIP SETUP ab 75, WATCH ab 65. „Ausgeschlossen" ist keine schlechte Note: Der Titel wurde gar nicht erst bewertet — zu klein oder ohne Fundamentaldaten.' },
  { key: 'score', label: 'Score', align: 'right', mono: true, minWidth: '110px',
    render: (row) => row.excluded
      ? <span className={s.fehlt}>—</span>
      : <Punkte punkte={row.score} maximum={row.maximum} soll={100} />,
    help: 'Die Summe aus vier Blöcken minus Penalty, gegen das bei dieser Historie ERREICHBARE. Ein Score von 60 bei einem Maximum von 70 ist etwas anderes als 60 von 100 — die Fahne zeigt an, dass Terme mangels Daten ausfielen.' },
  { key: 'fundamental', label: 'Fundamental', align: 'right', mono: true,
    minWidth: '110px', hideOn: 'tablet',
    render: (row) => <Blockpunkte zeile={row} block={row.fundamental}
                                  soll={30} />,
    help: 'Bis 30 Punkte: Größe, Umsatzwachstum, Gewinn, freier Cashflow. Fehlen drei dieser Terme, wird der Titel gar nicht bewertet — ohne Gewinn- und Cashflow-Angabe lässt sich nicht sagen, ob ein Unternehmen gesund ist.' },
  { key: 'selloff', label: 'Abverkauf', align: 'right', mono: true,
    minWidth: '100px', hideOn: 'tablet',
    render: (row) => <Blockpunkte zeile={row} block={row.selloff}
                                  soll={20} />,
    help: 'Bis 20 Punkte: Abstand zum Jahreshoch und Monatsperformance. Eine GLOCKE, kein Hang — rund −20 % bringen am meisten, −50 % fast nichts. Zu tief gefallen ist kein besseres Signal, sondern ein gefährliches.' },
  { key: 'bottoming', label: 'Boden', align: 'right', mono: true,
    minWidth: '100px',
    render: (row) => <Blockpunkte zeile={row} block={row.bottoming}
                                  soll={35} />,
    help: 'Bis 35 Punkte: RSI-Stand, RSI-Richtung, Kursstruktur, Volumen. Der größte Block — hier entscheidet sich, ob der Abverkauf ausläuft.' },
  { key: 'liquidity', label: 'Liquidität', align: 'right', mono: true,
    minWidth: '100px', hideOn: 'tablet',
    render: (row) => <Blockpunkte zeile={row} block={row.liquidity}
                                  soll={15} />,
    help: 'Bis 15 Punkte: Tagesumsatz in Millionen Dollar. Was sich nicht handeln lässt, nützt kein Setup.' },
  { key: 'penalty_points', label: 'Penalty', align: 'right', mono: true,
    minWidth: '90px',
    render: (row) => row.penalty_points === 0
      ? <span className={s.fehlt}>—</span>
      : <span className={s.penalty}
              title={row.penalty_reasons.join(' · ')}>
          {row.penalty_points}
        </span>,
    help: 'Bis −20 Punkte für Warnzeichen eines fallenden Messers: neues 20-Tage-Tief, fallender RSI, drei rote Tage, Abwärtsvolumen, unter fallender EMA20, offene Kurslücke. Die Gründe beim Überfahren.' },
  { key: 'rsi', label: 'RSI', align: 'right', mono: true, minWidth: '80px',
    render: (row) => komma(row.rsi),
    help: 'Relative Stärke über 14 Tage nach Wilder — derselbe Wert, den jede Charting-Software zeigt. Unter 30 gilt als überverkauft.' },
  { key: 'drawdown_pct', label: 'zum Hoch', align: 'right', mono: true,
    minWidth: '100px',
    render: (row) => <span className={s.negativ}>
                       {mitVorzeichen(row.drawdown_pct)} %
                     </span>,
    help: 'Abstand zum höchsten Kurs der letzten 52 Wochen, in Prozent.' },
  { key: 'perf_1m_pct', label: '1 Monat', align: 'right', mono: true,
    minWidth: '100px', hideOn: 'tablet',
    render: (row) => `${mitVorzeichen(row.perf_1m_pct)} %`,
    help: 'Kursveränderung über 21 Handelstage.' },
  { key: 'bars', label: 'Bars', align: 'right', mono: true, minWidth: '80px',
    hideOn: 'tablet',
    help: 'Wie viele Handelstage vorliegen. Worauf alles beruht — unter etwa 60 trägt keine Auswertung, die auf Durchschnitten fußt.' },
]

/** Die vier Blöcke eines Titels, mit jedem einzelnen Term. */
function Aufschluesselung({ zeile }: { zeile: DipAssessment }) {
  if (zeile.excluded) {
    return (
      <Card title={`${zeile.name} — nicht bewertet`} span={12}>
        <p className={s.grund}>{zeile.exclusion_reason}</p>
        <p className={s.hinweis}>
          Ausgeschlossen heißt NICHT „schlecht". Der Titel wurde gar nicht
          erst bewertet — ein Score wäre eine Aussage, die die Daten nicht
          hergeben.
        </p>
      </Card>
    )
  }
  const bloecke: DipBlock[] = [zeile.fundamental, zeile.selloff,
                               zeile.bottoming, zeile.liquidity]
  return (
    <Card title={`${zeile.name} — woraus sich der Score ergibt`} span={12}
          help="Jeder Term mit seinen Punkten, dem erreichbaren Maximum und der Begründung. Ein Term ohne Punkte ist nicht bewertbar — die Daten fehlen.">
      <div className={s.bloecke}>
        {bloecke.map((block) => (
          <section key={block.name} className={s.block}>
            <h4>
              {BLOCK_NAMEN[block.name] ?? block.name}
              <span className={s.blockPunkte}>
                {block.points}/{block.maximum}
              </span>
            </h4>
            <ul className={s.terme}>
              {block.terms.map((term) => (
                <li key={term.name} className={s.term}>
                  <span className={s.termName}>{term.name}</span>
                  <span className={term.points === null
                    ? s.termOhne : s.termPunkte}>
                    {term.points === null ? '—' : term.points}
                    <span className={s.von}>/{term.maximum}</span>
                  </span>
                  <span className={s.termGrund}>
                    {/*
                      DER BETRAG ZUERST, die Erläuterung dahinter. Er steht
                      als eigenes Feld und nicht mehr im Text — nur deshalb
                      lässt er sich umrechnen.
                    */}
                    {term.value && (
                      <span className={s.termBetrag}>
                        <Geld wert={term.value} schreibe={alsGrossbetrag}
                              links />
                      </span>
                    )}
                    {grund(term.reason)}
                    {/*
                      Beim 52W-Abstand steht das Hoch dabei, gegen das
                      gemessen wurde — mit seinem Tag. „−17,8 % zum Hoch"
                      allein ist zweideutig: Ein Hoch von vor fünf Tagen
                      bedeutet Tag fünf eines Einbruchs, eines von vor acht
                      Monaten einen ausgereiften Abwärtstrend.
                    */}
                    {term.name === '52W-Abstand' && zeile.high_52w && (
                      <span className={s.bezug}>
                        Hoch {geldtext(zeile.high_52w)}
                        {zeile.high_52w_on &&
                          ` am ${tag.format(new Date(zeile.high_52w_on))}`}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      {zeile.penalty_reasons.length > 0 && (
        <p className={s.grund}>
          <b>Penalty {zeile.penalty_points}:</b>{' '}
          {zeile.penalty_reasons.join(' · ')}
        </p>
      )}
      {zeile.penalty_unchecked.length > 0 && (
        <p className={s.hinweis}>
          Nicht prüfbar mangels Historie:{' '}
          {zeile.penalty_unchecked.join(' · ')}. Ein ungeprüftes Warnzeichen
          ist kein festgestelltes — es gibt dafür keinen Abzug.
        </p>
      )}
    </Card>
  )
}

const schluessel = (row: DipAssessment) => `${row.isin}-${row.exchange}`

export function BuyTheDipPage() {
  const allowed = useHasPermission(PERMISSION)
  const [gewaehlt, setGewaehlt] = useState<string | null>(null)
  const [sichtbar, setSichtbar] = useState<number | null>(SICHTBARE_ZEILEN)

  // `useCallback`, sonst liefe der Hook endlos nach: Eine bei jedem Rendern
  // neue Ladefunktion löst ihren eigenen Aufruf erneut aus.
  const { waehrung, wahl } = useZielwaehrung(allowed)

  const laden = useCallback(
    (signal: AbortSignal) => api.buyTheDip(
      waehrung ? { currency: [waehrung] } : {}, signal), [waehrung])
  const { item, loading, error, denied } = useItemQuery(laden, allowed)

  // Oben in der Kopfzeile, wie auf den anderen Ansichten. Sie ändert hier
  // ausdrücklich NUR die Anzeige: Der Score rechnet weiter in Dollar, weil
  // seine Schwellen Dollarbeträge sind.
  usePageActions(wahl)

  if (!allowed || denied) {
    return <PermissionNotice permission={PERMISSION}
                             subject="die Auswertungen" />
  }

  const zeilen = item?.rows ?? []
  const bewertet = zeilen.filter((z) => !z.excluded).length
  const offen = zeilen.find((z) => schluessel(z) === gewaehlt)

  return (
    <>
      <Card
        title="Buy the Dip"
        flush
        help="Abverkaufte Qualitätsaktien mit beginnender Bodenbildung — der Punktescore aus POC v1, unverändert übernommen. Anders als der Daily Stretch, der den heutigen Tag in ATR misst und mit Gates arbeitet, fragt dieser Screen nach der Jahreslage und addiert Punkte. Beide auf denselben Titeln zu sehen, zeigt den Unterschied der Verfahren."
        meta={item ? (
          <span className={s.fuss}>
            {zeilen.length} Titel, davon {bewertet} bewertet
            {item.trading_date &&
              <> · Handelstag {tag.format(new Date(item.trading_date))}</>}
            <span className={s.hinweis}>
              Bei jedem Abruf neu gerechnet — es gibt keine gespeicherten
              Ergebnisse.
            </span>
          </span>
        ) : undefined}
      >
        {error && <ErrorBox>{error}</ErrorBox>}

        <DataTable
          columns={COLUMNS}
          rows={zeilen}
          rowKey={schluessel}
          loading={loading}
          visibleRows={sichtbar}
          onVisibleRows={setSichtbar}
          onRowClick={(row) => setGewaehlt(
            gewaehlt === schluessel(row) ? null : schluessel(row))}
          // Die aufgeklappte Zeile markieren. Die Aufschlüsselung steht
          // UNTERHALB der Tabelle — ohne Markierung wäre nicht erkennbar,
          // zu welchem Titel sie gehört.
          rowAttrs={(row): Record<string, string> =>
            schluessel(row) === gewaehlt ? { 'data-active': 'true' } : {}}
          empty={
            <EmptyState title="keine Titel">
              Gerechnet wird über die aktiven Einträge der Watchlist. Solange
              dort nichts steht, bleibt die Liste leer.
            </EmptyState>
          }
        />
      </Card>

      {offen && <Aufschluesselung zeile={offen} />}
    </>
  )
}
